import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Paper,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTournamentContext } from "../context/TournamentContext";
import {
  createTournament,
  updateTournament,
  uploadTournamentFile,
} from "../api/tournamentApi";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchTeamDetailswoyhoutPagination } from "../api/teamsApi";
import { getApiErrorMessage } from "../utils/apiError";

const isOnlyWhitespace = (value) => {
  return value !== "" && value.trim() === "";
};

const validateMaxParticipants = (value) => {
  if (value === "" || value === undefined) {
    return "Max participants is required";
  }

  const num = Number(value);

  if (isNaN(num)) {
    return "Invalid number";
  }

  // ✅ FIX: 1 ko reject karo
  if (num < 2) {
    return "Must be a power of 2 (e.g. 2, 4, 8, 16...)";
  }

  if ((num & (num - 1)) !== 0) {
    return "Must be a power of 2 (e.g. 2, 4, 8, 16...)";
  }

  return "";
};
const TournamentForm = () => {
  const getDefaultStartTime = () => {
    const date = new Date();
    date.setMinutes(date.getMinutes() + 3);
    return date;
  };
  const [name, setName] = useState("");
  const [status, setStatus] = useState("Scheduled");
  const [startTime, setStartTime] = useState(getDefaultStartTime());
  const [description, setDescription] = useState("");
  const [team, setTeam] = useState("");
  const [tournamentDuration, setTournamentDuration] = useState("50");
  const [format, setFormat] = useState("Knockout");
  const [triggerTime, setTriggerTime] = useState(getDefaultStartTime()); // Same as startTime initially
  // const [participationFee, setParticipationFee] = useState(0);
  // const [minPrizeMoney, setMinPrizeMoney] = useState(0);
  const [tnc, setTnc] = useState("");
  const [matchDuration, setMatchDuration] = useState(5);
  const [minParticipants, setMinParticipants] = useState(2);
  const [maxParticipants, setMaxParticipants] = useState("8");
  const [numberOfRounds, setNumberOfRounds] = useState(1);
  const [error, setError] = useState("");
  const { setTournaments } = useTournamentContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [userTeam, setTeams] = useState([]);
  const tournamentData = location.state?.tournament;
  const [maxParticipantsError, setMaxParticipantsError] = useState("");
  const [startTimeError, setStartTimeError] = useState("");
  const [matchDurationError, setMatchDurationError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [formatError, setFormatError] = useState("");
  const [tncError, setTncError] = useState("");  

  const getTeams = async () => {
    try {
      const data = await fetchTeamDetailswoyhoutPagination();
      setTeams(data.data.data);
      setTeam(data.data.data[0].id)
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  useEffect(() => {
    getTeams();
    if (tournamentData) {
      setName(tournamentData.name || "");
      setStatus(tournamentData.status || "Scheduled");
      setStartTime(new Date(tournamentData.start_time));
      setTriggerTime(new Date(tournamentData.trigger_time)); // Initializing with tournament data if available
      setDescription(tournamentData.description || "");
      // setTeam(
      //   tournamentData.setTeam || "Default Team"
      // );
      setTournamentDuration(tournamentData.tournament_duration || "");
      setFormat(tournamentData.format || "");
      // setParticipationFee(tournamentData.participation_fee || 0);
      // setMinPrizeMoney(tournamentData.min_prize_money || 0);
      setTnc(tournamentData.tnc || "");
      setMatchDuration(tournamentData.match_duration || 0);
      setMinParticipants(tournamentData.min_participants || 2);
      setMaxParticipants(tournamentData.max_participants || 100);
      setNumberOfRounds(tournamentData.number_of_rounds || 1);
    }
  }, [tournamentData]);




  const validateStartTime = (time) => {
    if (!time) {
      return "Start time is required";
    }
    const selectedDate = new Date(time);
    if (isNaN(selectedDate.getTime())) {
      return "Invalid date/time";
    }
    const diffMinutes = (selectedDate - new Date()) / (1000 * 60);
    if (diffMinutes < 3) {
      return "Choose a time that is greater than 3 minutes from current time";
    }
    return "";
  };

  const handleStartTimeChange = (newStartTime) => {
    setStartTime(newStartTime);
    setTriggerTime(newStartTime);
    const err = validateStartTime(newStartTime);
    setStartTimeError(err);
  };

  const handleBackClick = () => {
    navigate("/dashboard");
  };

  const getTournamentDuration = () => {
    const value = Math.log2(Math.pow(2, Math.ceil(Math.log2(maxParticipants))));
    return 2 * (value * matchDuration) + 2 * (value - 1);
  };

  const handleSubmit = async (e) => {
    if (!matchDuration || matchDuration <= 0) {
      setMatchDurationError("Match duration must be greater than 0");
      return;
    }

    if (!maxParticipants || maxParticipants <= 0) {
      setMaxParticipantsError("Max participants must be greater than 0");
      return;
    }

    if ((maxParticipants & (maxParticipants - 1)) !== 0) {
      setMaxParticipantsError("Max participants must be power of 2");
      return;
    } 
    if (isOnlyWhitespace(description)) {
      setDescriptionError("Description cannot contain only whitespace.");
      return;
    } else {
      setDescriptionError("");
    }

    if (isOnlyWhitespace(format)) {
      setFormatError("Format cannot contain only whitespace.");
      return;
    } else {
      setFormatError("");
    }

    if (isOnlyWhitespace(tnc)) {
      setTncError("Terms and conditions cannot contain only whitespace.");
      return;
    } else {
      setTncError("");
    }
    if (matchDuration <= 0) {
      setMatchDurationError("Match duration must be greater than 0.");
      return;
    }
    e.preventDefault();
    setError("");

    const timeErr = validateStartTime(startTime);
    if (timeErr) {
      setStartTimeError(timeErr);
      return;
    }
    const token = localStorage.getItem("accessToken");

    try {
      const tDuration = getTournamentDuration();
      //  setTournamentDuration( getTournamentDuration());
      const tournamentDataToSubmit = {
        name,
        status,
        startTime,
        description,
        team,
        tournamentDuration: tDuration,
        format,
        triggerTime,
        // participationFee,
        // minPrizeMoney,
        tnc,
        matchDuration,
        minParticipants,
        maxParticipants,
        numberOfRounds: Math.log2(Math.pow(2, Math.ceil(Math.log2(maxParticipants))))
      };


      console.log(tournamentDataToSubmit, "tournamentDataToSubmit")

      if (tournamentData) {
        // Update tournament logic
        await updateTournament(
          tournamentDataToSubmit,
          tournamentData.id,
          token
        );
        setTournaments((prev) =>
          prev.map((t) =>
            t.id === tournamentData.id ? { ...t, ...tournamentDataToSubmit } : t
          )
        );
        alert("Tournament updated successfully");
        navigate("/fetch-tournaments");
      } else {
        // Create new tournament logic
        const createdTournament = await createTournament(
          tournamentDataToSubmit,
          token
        );
        setTournaments((prev) => [...prev, createdTournament]);
        alert("Tournament created successfully");
        // Reset form only after creation, not after update
        resetForm();
        navigate("/fetch-tournaments");
      }
    } catch (error) {
      console.error("Error creating or updating tournament:", error);
      const errMsg = getApiErrorMessage(error, "Error creating/updating tournament. Please try again.");
      setError(errMsg);
      alert(errMsg);
    }
  };

  const handleUploadClick = async (e) => {
    try {
      const file = e.target.files[0];

      if (!file) {
        alert("Please select a file to upload");
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert("File size should not exceed 5MB");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      
      const token = localStorage.getItem("accessToken");

      const result = await uploadTournamentFile(formData, token);

      if (result.status) {
        alert("File uploaded successfully");
        navigate("/fetch-tournaments");
      } else {
        // Handle specific error messages from the server
        const errorMessage =
          result.message ||
          result.msg ||
          "Failed to upload tournament file. Please try again.";
        alert(errorMessage);
      }
    } catch (error) {
      console.error("Error uploading tournament file:", error);
      alert(getApiErrorMessage(error, "Failed to upload tournament file."));
    } finally {
      e.target.value = "";
    }
  };


  const handleDownloadCSV = () => {
    const headers = [
      "start_time",
      "max_participants",
      "match_duration",
      "description",
      "format",
      "state",
      "tnc",
      "Team",
    ];

    const instructions = [
      "# Fill one row per tournament. Delete or replace the example row below.",
      "# start_time: use YYYY-MM-DD HH:mm (24-hour) and schedule in the future.",
      "# max_participants: use a power of two such as 8, 16, 32, 64.",
      "# match_duration: positive integer in minutes.",
      "# Team: enter the exact team name as shown in the dashboard.",
    ];

    const exampleRow = [
      "YYYY-MM-DD HH:mm",
      "16",
      "20",
      "Example Tournament Name",
      "Swiss Rapid",
      "Scheduled",
      "Any special instructions or rules",
      "Exact Team Name",
    ];

    const csvContent = [...instructions, headers.join(","), exampleRow.join(",")].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", "tournament_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Reset form function after creation
  const resetForm = () => {
    setName("");
    setStatus("Scheduled");
    const defaultTime = getDefaultStartTime();
    setStartTime(defaultTime);
    setTriggerTime(defaultTime);
    setDescription("");
    setTeam(userTeam?.[0]?.id || "");
    setTournamentDuration("");
    setFormat("");
    // setParticipationFee(0);
    // setMinPrizeMoney(0);
    setTnc("");
    setMatchDuration(0);
    setMinParticipants(2);
    setMaxParticipants(100);
    setNumberOfRounds(1);
  };

  const currentDate = new Date();
  const minDateTime = new Date(Date.now() + 3 * 60000);
  const isHidden = true;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper elevation={3} sx={{ padding: 4 }}>
        <h2 style={{ textAlign: "center", marginBottom: "20px", color: "#333" }}>
          {tournamentData ? "Edit Tournament" : "Create Tournament"}
        </h2>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={handleBackClick}
          >
            Back to Dashboard
          </Button>
          {!tournamentData && (
            <Box display="flex" alignItems="center" gap={2}>
              <input
                type="file"
                accept=".csv"
                id="csv-upload"
                style={{ display: "none" }}
                onChange={handleUploadClick}
              />
              <label htmlFor="csv-upload" style={{ margin: 0 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  component="span"
                >
                  Upload Tournament Sheet
                </Button>
              </label>

              <Button
                variant="outlined"
                color="primary"
                onClick={handleDownloadCSV}
              >
                Download Format
              </Button>
            </Box>
          )}
        </Box>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            required
            disabled
            sx={{ marginBottom: 2 }}
          />
          <Box sx={{ marginBottom: 2 }}>
            <DateTimePicker
              label="Start Time"
              value={startTime}
              onChange={handleStartTimeChange}
              minDateTime={minDateTime}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!startTimeError}
                  helperText={startTimeError}
                />
              )}
            />
          </Box>
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => {
              const value = e.target.value.slice(0, 500);
              setDescription(value);

              if (isOnlyWhitespace(value)) {
                setDescriptionError("Description cannot contain only whitespace.");
              } else {
                setDescriptionError("");
              }
            }}
            inputProps={{
              maxLength: 500
            }}
            error={!!descriptionError}
            helperText={
              descriptionError ||
              `${description.length}/500`
            }
            FormHelperTextProps={{
              sx: {
                color: description.length >= 500 ? "#d32f2f" : undefined,
              },
            }}
            sx={{ marginBottom: 2 }}
          />
          <FormControl fullWidth sx={{ marginBottom: 2 }} required>
            <InputLabel id="team-select-label">
              Team
            </InputLabel>
            <Select
              labelId="team-select-label"
              id="team-select"
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              label="Team"
            >
              {userTeam.map((teamItem) => (
                <MenuItem key={teamItem.id} value={teamItem.id}>
                  {teamItem.name}
                </MenuItem>
              ))}
            </Select>
            {!team && (
              <FormHelperText error>Team is required.</FormHelperText>
            )}
          </FormControl>
          <TextField
            fullWidth
            label="Match Duration (minutes)"
            type="number"
            value={matchDuration}
            onChange={(e) => {
              const value = e.target.value;

              if (value === "") {
                setMatchDuration("");
                setMatchDurationError("Match duration is required");
                return;
              }

              const num = Number(value);

              if (num <= 0) {
                setMatchDuration(num);
                setMatchDurationError("Must be greater than 0");
              } else {
                setMatchDuration(num);
                setMatchDurationError("");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "-" || e.key === "e") {
                e.preventDefault();
              }
            }}
            inputProps={{
              min: 1,
              style: {
                MozAppearance: "textfield",
              },
            }}
            sx={{
              marginBottom: 2,
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
              {
                WebkitAppearance: "none",
                margin: 0,
              },
            }}       
            error={!!matchDurationError}
            helperText={matchDurationError}
            required            
          />
          <TextField
            fullWidth
            label="Format"
            value={format}
            onChange={(e) => {
              const value = e.target.value.slice(0, 100);
              setFormat(value);

              if (isOnlyWhitespace(value)) {
                setFormatError("Format cannot contain only whitespace.");
              } else {
                setFormatError("");
              }
            }}
            inputProps={{
              maxLength: 100
            }}
            error={!!formatError}
            helperText={
              formatError ||
              `${format.length}/100`
            }
            FormHelperTextProps={{
              sx: {
                color: format.length >= 100 ? "#d32f2f" : undefined,
              },
            }}
            required
            sx={{ marginBottom: 2 }}
          />
          {!isHidden && (
            <Box sx={{ marginBottom: 2 }}>
              <DateTimePicker
                label="Trigger Time"
                value={triggerTime}
                onChange={() => { }}
                disabled
                renderInput={(params) => <TextField {...params} />}
              />
            </Box>
          )}
          <TextField
            fullWidth
            label="Terms and Conditions"
            value={tnc}
            onChange={(e) => {
              const value = e.target.value;
              setTnc(value);

              if (isOnlyWhitespace(value)) {
                setTncError("Terms and conditions cannot contain only whitespace.");
              } else {
                setTncError("");
              }
            }}
            error={!!tncError}
            helperText={tncError}
            sx={{ marginBottom: 2 }}
          />
          <TextField
            fullWidth
            label="Maximum Participants"
            type="number"
            value={maxParticipants}
            onChange={(e) => {
              const value = e.target.value;
              setMaxParticipants(value);

              const errorMsg = validateMaxParticipants(value);
              setMaxParticipantsError(errorMsg);
            }}
            onKeyDown={(e) => {
              if (e.key === "-" || e.key === "e") {
                e.preventDefault();
              }
            }}
            inputProps={{
              min: 1,
              style: {
                MozAppearance: "textfield",
              },
            }}
            sx={{
              marginBottom: 2,
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
              {
                WebkitAppearance: "none",
                margin: 0,
              },
            }}
            error={!!maxParticipantsError}
            helperText={maxParticipantsError}
            required
          />      
          {error && <p style={{ color: "red" }}>{error}</p>}
          <Box
            display="flex"
            justifyContent="flex-end"
            mt={3}
          >
            <Button
              type="submit"
              variant="contained"
              color="primary"
            >
              {tournamentData
                ? "Update Tournament"
                : "Create Tournament"}
            </Button>
          </Box>
        </form>
      </Paper>
    </LocalizationProvider>
  );
};

export default TournamentForm;
